import { useEffect } from "react";
import {
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { APIError, ValidationError } from "@/core/api/errors";
import { NetworkDetector } from "@/services/networkDetector";
import {
  API_QUERY_STALE_TIME,
  API_QUERY_CACHE_TIME,
  API_QUERY_MAX_RETRIES,
  API_QUERY_RETRY_DELAY_MS,
  API_RECONNECT_RECOVERY_DELAY_MS,
} from "@/constants";

export type UseApiQueryOptions<T> = Omit<
  UseQueryOptions<T>,
  "queryFn" | "gcTime" | "retry"
> & {
  /**
   * Enable offline-first mode for this query.
   * When enabled:
   * - Uses cached data when offline (networkMode: "offlineFirst")
   * - Doesn't refetch on network reconnect if query succeeded
   * - Refetches on reconnect if query had an error (for recovery)
   * - Doesn't refetch on window focus
   *
   * Useful for list pages and data grids that should continue working offline.
   * Leave disabled (default) for detail/edit pages that need fresh data.
   */
  offlineFirst?: boolean;
  /**
   * Custom retry delay strategy. Returns milliseconds to wait before retry attempt.
   * Defaults to exponential backoff: 1s, 2s, 4s
   *
   * @example
   * // No delays for tests
   * retryDelay: () => 0
   *
   * @example
   * // Linear backoff: 200ms, 400ms, 600ms
   * retryDelay: (attempt) => attempt * 200
   *
   * @example
   * // Custom exponential: 500ms, 1s, 2s
   * retryDelay: (attempt) => Math.pow(2, attempt) * 500
   */
  retryDelay?: (attemptIndex: number) => number;
};

/**
 * Hook for fetching data with automatic retry on network failures
 *
 * Retry behavior:
 * - APIError (network/server): Retries up to 3 times with exponential backoff
 * - ValidationError (schema mismatch): No retry
 * - Supports optional offline-first mode for list queries
 * - Recovers from failed queries: Refetches on reconnect if query had an error
 *
 * @example
 * // Detail page - normal behavior, fetches fresh data on reconnect
 * const { data, isLoading } = useApiQuery({
 *   queryKey: ['stores', storeId],
 *   queryFn: async () => storeService.getById(storeId),
 * });
 *
 * @example
 * // List page - offline-first mode, shows cached data when offline
 * const { data, isLoading } = useApiQuery({
 *   queryKey: ['stores'],
 *   queryFn: async () => storeService.getList(),
 *   offlineFirst: true,
 * });
 */
export function useApiQuery<T>(
  options: UseApiQueryOptions<T> & {
    queryFn: () => Promise<T>;
  },
): UseQueryResult<T, Error> {
  // Determine retry behavior based on error type
  const shouldRetry = (failureCount: number, error: Error): boolean => {
    // Don't retry ValidationError (contract issue)
    if (error instanceof ValidationError) {
      return false;
    }

    // Retry APIError up to 3 times (network/server issues)
    if (error instanceof APIError) {
      return failureCount < API_QUERY_MAX_RETRIES;
    }

    // Default: no retry for unknown errors
    return false;
  };

  // Use injected retryDelay or default to exponential backoff
  const getRetryDelay =
    options.retryDelay ??
    ((attemptIndex: number) => {
      return Math.pow(2, attemptIndex) * API_QUERY_RETRY_DELAY_MS; // 1s, 2s, 4s
    });

  // Merge with defaults, apply offline-first if enabled
  const finalOptions: UseQueryOptions<T> = {
    ...options,
    staleTime: options.staleTime ?? API_QUERY_STALE_TIME,
    gcTime: API_QUERY_CACHE_TIME,
    retry: shouldRetry,
    retryDelay: getRetryDelay,
    // Offline-first mode (optional, on-demand)
    ...(options.offlineFirst && {
      networkMode: "offlineFirst",
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }),
  };

  const query = useQuery(finalOptions as UseQueryOptions<T>);

  // Subscribe to network status changes for automatic reconnect recovery
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleStatusChange = ({ isOnline }: { isOnline: boolean }) => {
      // When online and there was a failed query, retry
      if (isOnline && query.isError && !query.isFetching) {
        // Clear previous timeout if it exists
        if (timeoutId) clearTimeout(timeoutId);
        // Use setTimeout to ensure the network is stable before retrying
        timeoutId = setTimeout(() => {
          if (!query.isFetching && NetworkDetector.getStatus().isOnline) {
            query.refetch();
          }
        }, API_RECONNECT_RECOVERY_DELAY_MS);
      }
    };

    NetworkDetector.on("online", handleStatusChange);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      NetworkDetector.off("online", handleStatusChange);
    };
  }, [query.isError, query.isFetching, query]);

  return query;
}
